import { EducationItem } from '@/api/financial/application/read-application'
import { isMarried } from '@/pages/credit/journeys/allowance/allowance-flow'

/** Mesmas etapas da Mesada: Simulação, Dados, Documentos, Em análise e Contrato. */
export const personalLoanSteps = [
  'valor',
  'eu',
  'end',
  'civil',
  'uso',
  'grad',
  'espec',
  'conta',
  'docs',
  'analise',
  'proposta',
  'assinar',
  'fim',
] as const

export type PersonalLoanStep = (typeof personalLoanSteps)[number]

const dataSteps = new Set<PersonalLoanStep>([
  'eu',
  'end',
  'civil',
  'uso',
  'grad',
  'espec',
  'conta',
])

export function stageOf(step: PersonalLoanStep) {
  if (step === 'valor') return 0
  if (dataSteps.has(step)) return 1
  if (step === 'docs') return 2
  if (step === 'analise') return 3
  return 4
}

export type PersonalLoanFacts = {
  hasSimulation: boolean
  status: string
  serverStep: string
  completed: Partial<Record<PersonalLoanStep, boolean>>
}

export type DoctorPerson = {
  birthdate?: string | null
  gender?: string | null
  marital_status?: string | null
  politically_exposed?: boolean | null
  us_person?: boolean | null
  spouse_name?: string | null
  spouse_tax_id?: string | null
  monthly_income?: unknown
  credit_purposes?: string[]
  education?: EducationItem[] | null
  address?: unknown[]
  bank_account?: unknown[]
}

export const requiredDocuments = ['personal', 'proof_of_income'] as const

export const documentList = [
  'personal',
  'address',
  'proof_of_income',
  'income_tax_return',
] as const

export function hasGraduation(education: EducationItem[] | null | undefined) {
  return Boolean(
    education?.some((item) => item.type === 'graduation' && item.institution),
  )
}

export function isStepComplete(
  step: PersonalLoanStep,
  person: DoctorPerson | undefined,
  extras: { hasSimulation?: boolean; documents?: string[] } = {},
) {
  switch (step) {
    case 'valor':
      return Boolean(extras.hasSimulation)
    case 'eu':
      return Boolean(person?.birthdate && person.gender)
    case 'end':
      return Boolean(person?.address?.length)
    case 'civil': {
      if (!person?.marital_status) return false
      if (person.politically_exposed == null || person.us_person == null)
        return false
      if (
        isMarried(person.marital_status) &&
        (!person.spouse_name || !person.spouse_tax_id)
      ) {
        return false
      }
      return true
    }
    case 'uso':
      return (
        person?.monthly_income != null &&
        person.monthly_income !== '' &&
        Boolean(person.credit_purposes?.length)
      )
    case 'grad':
      return hasGraduation(person?.education)
    case 'espec':
      return hasGraduation(person?.education)
    case 'conta':
      return Boolean(person?.bank_account?.length)
    case 'docs':
      return requiredDocuments.every((type) => extras.documents?.includes(type))
    default:
      return true
  }
}

export function resumeStep(facts: PersonalLoanFacts): PersonalLoanStep {
  if (facts.status === 'done') return 'fim'
  if (facts.status === 'denied') return 'analise'
  if (
    facts.serverStep === 'agreement_signature' ||
    facts.serverStep === 'credit_note_signature'
  ) {
    return 'assinar'
  }
  if (facts.serverStep === 'proposal' || facts.status === 'approved')
    return 'proposta'
  if (
    facts.status === 'analysing' ||
    facts.serverStep === 'committee_analysis' ||
    facts.serverStep === 'documents_analysis'
  ) {
    return 'analise'
  }
  if (facts.serverStep === 'proponets_documents' || facts.status === 'review') {
    return 'docs'
  }
  // a pré-análise roda ao salvar a simulação: sem ela, a simulação é refeita
  if (!facts.hasSimulation || facts.serverStep === 'pre_analysis')
    return 'valor'

  const nextData = personalLoanSteps.find(
    (step) => dataSteps.has(step) && !facts.completed[step],
  )
  return nextData ?? 'docs'
}

export function resolveStep(
  requested: string | undefined,
  facts: PersonalLoanFacts,
): PersonalLoanStep {
  const resume = resumeStep(facts)
  const requestedStep = personalLoanSteps.find((step) => step === requested)
  if (!requestedStep) return resume
  // depois da pré-análise, a simulação só pode ser revista enquanto os dados não foram enviados
  if (requestedStep === 'valor') {
    return dataSteps.has(resume) || resume === 'valor' ? 'valor' : resume
  }
  if (
    personalLoanSteps.indexOf(requestedStep) > personalLoanSteps.indexOf(resume)
  ) {
    return resume
  }
  return requestedStep
}

export function stepProgress(step: PersonalLoanStep) {
  const inStage = personalLoanSteps.filter(
    (item) => stageOf(item) === stageOf(step),
  )
  const index = Math.max(inStage.indexOf(step), 0)
  return { current: index + 1, total: inStage.length }
}
